import MuiDrawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import type { ReactNode } from 'react';
import FeaturesActiveChip from '@/feature/features/FeaturesActiveChip';
import { useTexts } from '@/feature/lang';
import { track } from '@/feature/tracking';
import { Icon } from '@/icon/Icon';
import { mainNavItems } from '@/view/mainNavItems';
import { appBarHeight } from '../AppBar/AppBar';
import { Box } from '../Box';
import IconButton from '../Button/IconButton';
import { DevChip } from '../Chip/Chip';
import Divider from '../Divider';
import { Navigation } from '../Navigation';
import Tooltip from '../Tooltip';
import { ActAsCustomerInfo } from './ActAsCustomer';
import { drawerWidth, railWidth, transitionIn, transitionOut } from './layout';
import { useRail } from './useRail';

const Drawer = () => {
  const [rail] = useRail();

  return (
    <MuiDrawer
      variant="permanent"
      sx={(theme) => ({
        flexShrink: 0,
        whiteSpace: 'nowrap',
        display: 'flex',
        flexDirection: 'column',

        '& .MuiPaper-root': {
          overflow: 'hidden',
          borderRight: `1px solid ${theme.palette.divider}`,

          ...(!rail && {
            width: drawerWidth,
            transition: transitionIn(theme, 'width'),
          }),
          ...(rail && {
            width: railWidth,
            transition: transitionOut(theme, 'width'),
          }),
        },
      })}
    >
      <Toolbar sx={{ minHeight: `${appBarHeight}px !important` }} />
      <Box
        sx={{
          flex: 1,
          // Hide overflow when in rail. Use rail flyout with scrollbar to see all elements.
          overflowY: rail ? 'hidden' : 'auto',
          overflowX: 'hidden',
        }}
      >
        <Navigation navigation={mainNavItems} />
      </Box>
      <Chips />
      <ActAsCustomerInfo />
      <Divider />
      <Footer>
        <ToggleRail />
      </Footer>
    </MuiDrawer>
  );
};

export default Drawer;

function ToggleRail() {
  const texts = useTexts().component.drawer;
  const [rail, setRail] = useRail();
  const onSetRail = (rail: boolean) => {
    track('drawer_toggle_rail', { rail });
    setRail(rail);
  };

  return (
    <Tooltip
      title={rail ? texts.expand.tooltip : texts.collapse.tooltip}
      disableInteractive
    >
      <IconButton onClick={() => onSetRail(!rail)} size="large">
        <Icon name={rail ? 'chevron_right' : 'chevron_left'} />
      </IconButton>
    </Tooltip>
  );
}

const Chips = () => {
  const [rail] = useRail();

  return (
    <Box
      sx={[
        {
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          my: 1,
          mx: 2,
        },
        rail && {
          flexDirection: 'column',
          gap: 4,
          mx: 1,
        },
      ]}
    >
      <FeaturesActiveChip vertical={rail} />
      <DevChip vertical={rail} />
    </Box>
  );
};

const Footer = ({ children }: { children: ReactNode }) => {
  const [rail] = useRail();

  return (
    <Box
      display="flex"
      justifyContent="flex-end"
      flexDirection={rail ? 'column' : 'row'}
      alignItems="center"
      gap={0.5}
      margin={1}
    >
      {children}
    </Box>
  );
};
